// @file Integration permissions settings store
import { captureException } from '@@/bits/error_tracker'
import { User as UserApi } from '@@/dashboard/padlet_api'
import { useDashboardSettingsStore } from '@@/pinia/dashboard_settings'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useMicrosoftOneDrivePickerStore } from '@@/pinia/microsoft_onedrive_picker_store'
import type { UserOptions } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const useSettingsIntegrationPermissionsStore = defineStore('settingsIntegrationPermissions', () => {
  const dashboardSettingsStore = useDashboardSettingsStore()

  const userOptions = computed<UserOptions>(() => dashboardSettingsStore.userOptions)

  // State
  const googleDriveEmail = ref<string | null | undefined>(undefined)
  const microsoftOneDriveEmail = ref<string | null | undefined>(undefined)

  // Getters

  // Actions
  function initialize(): void {
    googleDriveEmail.value = userOptions.value.linkedGoogleDriveEmail ?? null
    microsoftOneDriveEmail.value = userOptions.value.linkedMicrosoftOneDriveEmail ?? null
  }

  // #region: Google Drive
  function setGoogleDriveEmail(email: string): void {
    dashboardSettingsStore.updateUserOptions({ linkedGoogleDriveEmail: email })
    googleDriveEmail.value = email
  }

  function clearGoogleDriveEmail(): void {
    dashboardSettingsStore.updateUserOptions({ linkedGoogleDriveEmail: undefined })
    googleDriveEmail.value = null
  }

  const removeGoogleDriveAuthorization = async (userId: number): Promise<void> => {
    // await an endpoint that will remove the google drive authorization
    try {
      await UserApi.removeGoogleDriveAuthorization(userId)
      clearGoogleDriveEmail()
    } catch (error) {
      useGlobalSnackbarStore().genericFetchError()
      captureException(error)
    }
  }

  // #endregion

  // #region: Microsoft OneDrive
  function setMicrosoftOneDriveEmail(email: string): void {
    microsoftOneDriveEmail.value = email
  }

  function clearMicrosoftOneDriveEmail(): void {
    microsoftOneDriveEmail.value = null
  }

  const removeMicrosoftOneDriveAuthorization = async (userId: number): Promise<void> => {
    // await an endpoint that will remove the microsoft one drive authorization
    try {
      await UserApi.removeMicrosoftOneDriveAuthorization(userId)
      clearMicrosoftOneDriveEmail()
      // Also clear the OneDrive picker store data
      useMicrosoftOneDrivePickerStore().clearMicrosoftOneDriveInfo()
    } catch (error) {
      useGlobalSnackbarStore().genericFetchError()
      captureException(error)
    }
  }
  // #endregion

  return {
    initialize,
    // google drive
    googleDriveEmail,
    removeGoogleDriveAuthorization,
    setGoogleDriveEmail,
    // microsoft one drive
    microsoftOneDriveEmail,
    removeMicrosoftOneDriveAuthorization,
    setMicrosoftOneDriveEmail,
  }
})
export { useSettingsIntegrationPermissionsStore }
